// Club states mapping for getting club names and default zip codes
const CLUB_STATES: Record<
  string,
  { fullState: string; club: string; zipCode: string; clubState: string; clubCode: string }
> = {
  AL: { fullState: 'Alabama', club: 'alabama', zipCode: '35004', clubState: 'AL', clubCode: '1' },
  ACMO: { fullState: 'Mississippi', club: 'autoclubmo', zipCode: '38601', clubState: 'ACMO', clubCode: '65' },
  CA: { fullState: 'California', club: 'calif', zipCode: '92626', clubState: 'CA', clubCode: '4' },
  EC: { fullState: 'Ohio', club: 'eastcentral', zipCode: '43101', clubState: 'EC', clubCode: '215' },
  HI: { fullState: 'Hawaii', club: 'hawaii', zipCode: '96701', clubState: 'HI', clubCode: '018' },
  NM: { fullState: 'New Mexico', club: 'newmexico', zipCode: '87001', clubState: 'NM', clubCode: '601' },
  NNE: { fullState: 'Maine', club: 'northernnewengland', zipCode: '03031', clubState: 'NNE', clubCode: '36' },
  TX: { fullState: 'Texas', club: 'texas', zipCode: '73344', clubState: 'TX', clubCode: '252' },
  TW: { fullState: 'Virginia', club: 'tidewater', zipCode: '22432', clubState: 'TW', clubCode: '258' },
};

/**
 * Get default zip code for a club code
 *
 * @param clubCode - AAA club code (e.g., "252", "4", "018", "004", "1")
 * @returns Default zip code for the club, or null if not found
 *
 * @example
 * const zipCode = getDefaultZipForClub("252"); // Returns "73344" for Texas
 * const zipCode = getDefaultZipForClub("004"); // Returns "92626" for California
 * const zipCode = getDefaultZipForClub("4");   // Returns "92626" for California
 */
export function getDefaultZipForClub(clubCode: string): string | null {
  // Normalize clubCode by converting to integer and back to string to handle leading zeros
  // This allows matching '4', '04', and '004' to the same club
  const normalizedClubCode = parseInt(clubCode, 10).toString();

  const clubState = Object.values(CLUB_STATES).find((state) => {
    const stateClubCode = parseInt(state.clubCode, 10).toString();
    return stateClubCode === normalizedClubCode;
  });

  return clubState?.zipCode || null;
}

/**
 * Standalone function to create AAA zip code cookies programmatically
 * without requiring user input from a modal
 *
 * @param zipCode - 5-digit zip code string
 * @param deviceType - Optional device type: "PC" (default), "SP" (smartphone), or "TB" (tablet)
 * @returns Promise that resolves with cookie data or rejects with error
 *
 * @example
 * // Basic usage
 * createZipCookieFromCode("92626").then(data => {
 *   console.log("Cookies created for:", data.clubName);
 * });
 *
 * // With custom device type
 * createZipCookieFromCode("92626", "SP").then(data => {
 *   console.log("Mobile cookies created");
 * });
 */
export async function createZipCookieFromCode(
  zipCode: string,
  deviceType: 'PC' | 'SP' | 'TB' = 'PC'
): Promise<{
  zipCode: string;
  clubCode: string;
  clubName: string;
  clubState: string;
  fullState: string;
  fullClubName: string;
}> {
  const ZIP_API = 'https://api.ace.aaa.com/zip-router/';

  // Validate zip code format
  if (!zipCode || zipCode.length !== 5 || isNaN(parseInt(zipCode))) {
    throw new Error('Invalid zip code. Must be a 5-digit number.');
  }

  try {
    // Call the zip router API
    const response = await fetch(ZIP_API + zipCode);
    const data = await response.json();

    if (!data.clubs || data.clubs.length === 0) {
      throw new Error(`No club data found for zip code: ${zipCode}`);
    }

    const clubData = data.clubs[0];

    // Check if this is an ACE club (has fullState)
    if (!clubData.fullState) {
      throw new Error(`Zip code ${zipCode} is not in an ACE club territory`);
    }

    // Get club name from mapping
    const clubInfo = CLUB_STATES[clubData.clubState] || {
      club: clubData.clubState.toLowerCase(),
      fullState: clubData.fullState,
    };

    // Prepare cookie expiration date
    const expDate = new Date();
    // All cookies expire in 1 hour
    expDate.setHours(expDate.getHours() + 1);

    // Create the cookie values
    const zipcodeValue = `${clubData.zipCode}|AAA|${clubData.clubCode}|${deviceType}`;
    const regionValue = clubInfo.club;
    const stateValue = clubData.fullState;
    const aceZipdataValue = `${clubData.clubState}|${clubData.zipCode}|${clubData.clubCode}`;

    // Write all cookies
    document.cookie = `zipcode=${zipcodeValue};expires=${expDate.toUTCString()}; domain=.aaa.com; secure; path=/`;
    document.cookie = `aaa-region=${regionValue};expires=${expDate.toUTCString()}; domain=.aaa.com; secure; path=/`;
    document.cookie = `aaa-state=${stateValue};expires=${expDate.toUTCString()}; domain=.aaa.com; secure; path=/`;
    document.cookie = `ace-zipdata=${aceZipdataValue};expires=${expDate.toUTCString()}; domain=.aaa.com; secure; path=/`;

    // Dispatch custom event for other parts of the app to listen to
    const zipEvent = new CustomEvent('zipRouteUpdate', {
      detail: {
        club: clubInfo.club,
        clubCode: clubData.clubCode,
        zipCode: clubData.zipCode,
      },
    });
    document.dispatchEvent(zipEvent);

    // Return the data
    return {
      zipCode: clubData.zipCode,
      clubCode: clubData.clubCode,
      clubName: clubInfo.club,
      clubState: clubData.clubState,
      fullState: clubData.fullState,
      fullClubName: clubData.fullClubName,
    };
  } catch (error) {
    console.error('Error creating zip cookie:', error);
    throw error;
  }
}

/**
 * Helper function to read existing zip code cookies
 *
 * @param cookieName - Name of the cookie to retrieve
 * @returns Cookie value or null if not found
 */
export function getCookie(cookieName: string): string | null {
  let cookieResult: string | null = null;
  const cookies = document.cookie.split(';');
  const cookieRegex = new RegExp(cookieName + '=', 'g');

  for (const cookieVal of cookies) {
    if (cookieVal.match(cookieRegex)) {
      const tempCookie = decodeURI(cookieVal);
      if (tempCookie.indexOf('=') > 0) {
        cookieResult = tempCookie.split('=')[1];
        break;
      }
    }
  }

  return cookieResult;
}

/**
 * Check if zip cookies already exist
 *
 * @returns Object with cookie status and values
 */
export function checkExistingZipCookies() {
  const zipcode = getCookie('zipcode');
  const region = getCookie('aaa-region');
  const state = getCookie('aaa-state');
  const aceZipdata = getCookie('ace-zipdata');

  return {
    exists: zipcode !== null,
    zipcode,
    region,
    state,
    aceZipdata,
    parsedZipcode:
      zipcode && typeof zipcode === 'string'
        ? {
            zip: zipcode.split('|')[0],
            clubCode: zipcode.split('|')[2],
            deviceType: zipcode.split('|')[3],
          }
        : null,
  };
}
